"use client";

import Link from "next/link";
import { memo, useCallback, useMemo } from "react";

import type { Service } from "@/types";
import { buildListItemFromService, ga4SelectItem } from "@/lib/analytics/ga4-ecommerce";
import { useCurrency } from "@/lib/contexts/CurrencyContext";
import { getThumbnail } from "@/lib/utils";
import { getListingCardImageUrl } from "@/lib/utils/image-proxy";
import { formatServiceDisplayTitle, servicePublicPath } from "@/lib/utils/service-title";
import { isSafeLinkHref } from "@/lib/security/safe-href";

import { Card, CardHeader } from "@/components/ui/card";
import { ServiceCardTitleClamp } from "@/components/cards/ServiceCardTitleClamp";
import { ServiceCardSubcategoryPills } from "@/components/cards/ServiceCardSubcategoryPills";
import GalleryRasterImage from "@/components/media/GalleryRasterImage";

function ServiceCard({
	service,
	listing,
}: {
	service: Service;
	listing?: { itemListId: string; itemListName: string; index: number };
}) {
	const userCurrency = useCurrency();

	const thumbUrl = getThumbnail(service.thumbnailImage);
	const displayTitle = formatServiceDisplayTitle(service.title);
	const listingPreview = useMemo(() => {
		const firstImage = (service.media ?? []).find((item) => {
			if (!item || item.type === "VIDEO") return false;
			const url = item.displayUrl?.trim() || item.url?.trim();
			return Boolean(url);
		});
		if (firstImage) {
			return {
				source: firstImage,
				fallbacks: [
					...(thumbUrl
						? [getListingCardImageUrl(thumbUrl, service.thumbnailDisplayUrl) ?? thumbUrl]
						: []),
				].filter(Boolean) as string[],
			};
		}
		if (thumbUrl) {
			return {
				source: getListingCardImageUrl(thumbUrl, service.thumbnailDisplayUrl) ?? thumbUrl,
				fallbacks: [] as string[],
			};
		}
		return null;
	}, [service.media, service.thumbnailDisplayUrl, thumbUrl]);

	const href = servicePublicPath(service);

	const reportListingSelect = useCallback(() => {
		if (!listing) return;
		const item = buildListItemFromService(service, userCurrency, listing.index);
		ga4SelectItem({
			item_list_id: listing.itemListId,
			item_list_name: listing.itemListName,
			items: [item],
		});
	}, [listing, service, userCurrency]);

	const mediaHeader = useMemo(() => {
		if (listingPreview) {
			const inner = (
				<GalleryRasterImage
					source={listingPreview.source}
					fallbackRasterSources={listingPreview.fallbacks}
					listingCard
					alt={displayTitle}
					loading="lazy"
					className="size-full object-cover object-center"
				/>
			);
			return isSafeLinkHref(href) ? (
				<Link
					href={href}
					onClick={reportListingSelect}
					className="relative block h-44 w-full shrink-0 cursor-pointer overflow-hidden rounded-t-lg bg-muted/40"
				>
					{inner}
				</Link>
			) : (
				<div className="relative block h-44 w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40">
					{inner}
				</div>
			);
		}
		return <div className="h-44 w-full shrink-0 rounded-t-lg bg-muted/40" aria-hidden />;
	}, [displayTitle, listingPreview, href, reportListingSelect]);

	if (!listingPreview) {
		return null;
	}

	return (
		<Card className="custom-box-shadow flex h-[calc(21rem-1rem)] w-full flex-col rounded-lg sm:h-[calc(21rem-2rem)] sm:w-80">
			<CardHeader
				aria-hidden
				className="h-44 w-full shrink-0 space-y-0 rounded-t-lg p-0"
			>
				{mediaHeader}
			</CardHeader>
			<Link
				href={href}
				onClick={reportListingSelect}
				className="flex min-h-0 flex-1 flex-col px-3 py-2 outline-none"
			>
				<div className="flex min-h-0 min-w-0 flex-1 flex-col">
					<div className="flex shrink-0 flex-col gap-0.5">
						<span className="flex h-[1.125rem] shrink-0 items-center text-xs leading-tight line-clamp-1 text-brand-primary">
							{service.categoryId?.title || "Uncategorized"}
						</span>
						<div className="text-sm leading-snug">
							<div className="flex h-[calc(3lh+0.25rem+2rem)] shrink-0 flex-col gap-1">
								<ServiceCardTitleClamp text={displayTitle} />
								<ServiceCardSubcategoryPills service={service} />
							</div>
						</div>
					</div>

					<div className="min-h-0 flex-1" aria-hidden />
				</div>
			</Link>
		</Card>
	);
}

export default memo(ServiceCard);
